import { Component, OnInit, AfterViewInit, ViewChild, TemplateRef, ChangeDetectorRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { OrgAdminSidebarComponent } from '../sidebar/org-admin-sidebar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { TableComponent, TableColumn, PaginationConfig, ModalComponent } from '../../shared/components/primeng';
import { AuthApiService, RolesApiService } from '@angular-workspace/services';
import { NetworkService } from '@angular-workspace/core';
import { environment } from '../../../environments/environment';
import { FormatService } from '../../shared/services/format.service';
import { KeycloakService } from 'keycloak-angular';

export interface UserData {
  userName: string;
  employeeId: string;
  jobTitle: string;
  currentRole: string;
  accountStatus: string;
  userCreatedAt: string;
  action?: string;
  originalData?: any;
}

@Component({
  selector: 'app-org-admin-view-all-users',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    OrgAdminSidebarComponent,
    PageHeaderComponent,
    TableComponent,
    ModalComponent,
  ],
  templateUrl: './view-all-users.component.html',
  styleUrl: './view-all-users.component.scss',
})
export class OrgAdminViewAllUsersComponent implements OnInit, AfterViewInit {
  @ViewChild('statusCell', { static: false }) statusCellTemplate!: TemplateRef<any>;
  @ViewChild('actionCell', { static: false }) actionCellTemplate!: TemplateRef<any>;

  constructor(
    private authApiService: AuthApiService,
    private rolesApiService: RolesApiService,
    private networkService: NetworkService,
    private formatService: FormatService,
    private cdr: ChangeDetectorRef,
    private keycloak: KeycloakService
  ) {}

  organisationId: string = '';
  openDropdownId: string | null = null;
  openDropdownRowIndex: number | null = null;
  showViewDetailsModal = false;
  selectedUserData: UserData | null = null;

  columns: TableColumn[] = [
    {
      key: 'userName',
      label: 'User Name',
      sortable: false,
      width: '15%'
    },
    {
      key: 'employeeId',
      label: 'Employee ID',
      sortable: false,
      width: '12%'
    },
    {
      key: 'jobTitle',
      label: 'Job Title',
      sortable: false,
      width: '15%'
    },
    {
      key: 'currentRole',
      label: 'Current Role',
      sortable: false,
      width: '13%'
    },
    {
      key: 'accountStatus',
      label: 'Account Status',
      sortable: false,
      width: '13%'
    },
    {
      key: 'userCreatedAt',
      label: 'User Created At',
      sortable: false,
      width: '18%'
    },
    {
      key: 'action',
      label: 'Action',
      sortable: false,
      width: '12%',
      align: 'left',
      fixed: 'right'
    }
  ];

  usersData: UserData[] = [];
  allUsersData: UserData[] = [];

  pagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100]
  };

  loading = false;
  emptyMessage = 'No users found';

  async ngOnInit(): Promise<void> {
    await this.waitForKeycloakReady();
    // First, get the organisation ID from user API
    await this.loadOrganisationId();
    // Then load the users
    await this.loadUsers();
  }

  private async waitForKeycloakReady(): Promise<void> {
    try {
      const keycloakInstance = this.keycloak.getKeycloakInstance();
      if (keycloakInstance?.authenticated && keycloakInstance?.token) return;
      const maxWaitTime = 5000, checkInterval = 100, startTime = Date.now();
      while (Date.now() - startTime < maxWaitTime) {
        const instance = this.keycloak.getKeycloakInstance();
        if (instance?.authenticated && instance?.token) return;
        await new Promise(resolve => setTimeout(resolve, checkInterval));
      }
    } catch (error) {
      console.error('Error waiting for Keycloak:', error);
    }
  }

  async loadOrganisationId(): Promise<void> {
    try {
      const userData = await this.authApiService.getUserInfo();
      if (userData && userData['result']) {
        const result = userData['result'];
        // Prefer root-level organisationId (API expects organisationId, not organisation.id)
        this.organisationId = result['organisationId'] ||
                             result['organisation_id'] ||
                             result['orgId'] ||
                             result['org_id'] ||
                             '';
        // Fallback: nested organisation object
        if (!this.organisationId && result['organisation']) {
          this.organisationId = result['organisation']['organisationId'] ||
                               result['organisation']['organisation_id'] ||
                               result['organisation']['id'] ||
                               '';
        }
      }
    } catch (error) {
      console.error('Error loading organisation ID:', error);
      this.organisationId = '';
    }
  }

  async loadUsers(): Promise<void> {
    if (!this.organisationId) {
      console.error('Organisation ID not available');
      this.loading = false;
      this.usersData = [];
      this.allUsersData = [];
      this.pagination = { ...this.pagination, totalItems: 0 };
      this.cdr.detectChanges();
      return;
    }

    this.loading = true;
    this.cdr.detectChanges(); // Update UI to show loading state
    
    try {
      const basePathUrl = (environment as any).basePathUrl || '';
      const authApiPath = (environment as any).authApiPath || 'iudx/v2/auth/';
      const page = this.pagination.currentPage;
      const size = this.pagination.pageSize;

      const url = `${basePathUrl}${authApiPath}organisations/${this.organisationId}/users?page=${page}&size=${size}`;

      console.log('Fetching users from URL:', url);

      const response = await this.networkService.get_api(
        url,
        undefined,
        false
      );

      // Map API response to UserData format
      let mappedData: UserData[] = [];
      let totalItems = 0;

      console.log('Users API response:', response);
      console.log('Response type:', typeof response);
      console.log('Is array:', Array.isArray(response));
      console.log('Response keys:', response ? Object.keys(response) : 'null/undefined');

      if (response) {
        // Handle different response structures
        if (response['result'] && Array.isArray(response['result'])) {
          console.log('Handling response.result as array');
          mappedData = response['result'].map((item: any) => this.mapUserItem(item));
          totalItems = response['paginationInfo']?.totalCount || response['totalCount'] || response['total'] || mappedData.length;
        } else if (response['data'] && Array.isArray(response['data'])) {
          console.log('Handling response.data as array');
          mappedData = response['data'].map((item: any) => this.mapUserItem(item));
          totalItems = response['paginationInfo']?.totalCount || response['totalCount'] || response['total'] || mappedData.length;
        } else if (response['result'] && response['result']['users'] && Array.isArray(response['result']['users'])) {
          console.log('Handling response.result.users as array');
          mappedData = response['result']['users'].map((item: any) => this.mapUserItem(item));
          totalItems = response['result']['paginationInfo']?.totalCount || response['paginationInfo']?.totalCount || response['totalCount'] || response['total'] || mappedData.length;
        } else if (response['result'] && response['result']['result'] && Array.isArray(response['result']['result'])) {
          console.log('Handling response.result.result as array');
          mappedData = response['result']['result'].map((item: any) => this.mapUserItem(item));
          totalItems = response['result']['paginationInfo']?.totalCount || response['paginationInfo']?.totalCount || response['totalCount'] || response['total'] || mappedData.length;
        } else if (Array.isArray(response)) {
          console.log('Handling response as direct array');
          mappedData = response.map((item: any) => this.mapUserItem(item));
          totalItems = mappedData.length;
        } else {
          console.warn('Unexpected response structure:', response);
          console.warn('Response structure details:', JSON.stringify(response, null, 2));
        }
      } else {
        console.warn('Response is null or undefined');
      }

      this.usersData = mappedData;
      this.allUsersData = [...mappedData];
      this.pagination = {
        ...this.pagination,
        totalItems: totalItems
      };

      if (mappedData.length === 0) {
        this.emptyMessage = 'No users found';
      }
      
      console.log('Mapped users data:', mappedData);
      console.log('Total items:', totalItems);
      console.log('Users data length:', this.usersData.length);
    } catch (error) {
      console.error('Error loading users:', error);
      this.usersData = [];
      this.allUsersData = [];
      this.pagination = {
        ...this.pagination,
        totalItems: 0
      };
      this.emptyMessage = 'No users found';
    } finally {
      this.loading = false;
      console.log('Loading set to false, usersData length:', this.usersData.length);
      // Force change detection to update the UI
      this.cdr.detectChanges();
    }
  }

  private mapUserItem(item: any): UserData {
    // Employee ID: prefer emp_id from API
    const employeeId = item.emp_id || item.employeeId || item.employee_id || item.empId || '';

    // Current Role: highest role from roles array
    let currentRole = '';
    if (item.roles && Array.isArray(item.roles) && item.roles.length > 0) {
      const highestRole = this.rolesApiService.getHighestRole(item.roles);
      if (highestRole) {
        currentRole = highestRole
          .split('-')
          .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(' ');
      } else {
        currentRole = (item.roles[0] || '').charAt(0).toUpperCase() + (item.roles[0] || '').slice(1).toLowerCase();
      }
    } else {
      const rawRole = item.currentRole || item.current_role || item.role || '';
      currentRole = this.formatService.toPascalCase(rawRole);
    }

    // User Created At: from created_at
    const createdAtRaw = item.created_at || item.createdAt || '';
    const userCreatedAt = createdAtRaw ? this.formatService.formatDate(createdAtRaw) : '';

    return {
      userName: item.userName || item.user_name || item.name || item.preferredUsername || item.preferred_username || '',
      employeeId,
      jobTitle: item.jobTitle || item.job_title || '',
      currentRole,
      accountStatus: item.accountStatus || item.account_status || item.status || (item.enabled !== undefined ? (item.enabled ? 'Active' : 'Inactive') : 'Active'),
      userCreatedAt,
      action: 'View',
      originalData: item
    };
  }

  getRowId(row: UserData): string {
    return row.originalData?.id || row.originalData?.userId || row.userName || JSON.stringify(row);
  }

  toggleActionDropdown(event: Event, row: UserData, rowIndex?: number): void {
    event.stopPropagation();
    event.preventDefault();
    const rowId = this.getRowId(row);
    const wasOpen = this.openDropdownId === rowId;
    this.openDropdownId = wasOpen ? null : rowId;
    this.openDropdownRowIndex = wasOpen ? null : (rowIndex ?? null);
    this.cdr.detectChanges();
    if (!wasOpen && this.openDropdownId === rowId) {
      setTimeout(() => {
        const button = (event.target as HTMLElement).closest('.action-btn') as HTMLElement;
        const dropdown = document.querySelector(`[data-dropdown-id="${rowId}"]`) as HTMLElement;
        if (button && dropdown) {
          const rect = button.getBoundingClientRect();
          dropdown.style.position = 'fixed';
          dropdown.style.top = `${rect.bottom + 4}px`;
          dropdown.style.left = `${rect.left}px`;
          dropdown.style.width = 'max-content';
          dropdown.style.minWidth = '120px';
        }
      }, 0);
    }
  }

  closeActionDropdown(): void {
    this.openDropdownId = null;
    this.openDropdownRowIndex = null;
  }

  isDropdownOpen(row: UserData): boolean {
    return this.openDropdownId === this.getRowId(row);
  }

  handleAction(action: string, row: UserData): void {
    this.closeActionDropdown();
    if (action === 'viewDetails') {
      this.selectedUserData = row;
      this.showViewDetailsModal = true;
      this.cdr.detectChanges();
    }
  }

  closeViewDetailsModal(): void {
    this.showViewDetailsModal = false;
    this.selectedUserData = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (target.closest('app-modal') || target.closest('.p-dialog') || target.closest('.p-dialog-mask')) {
      return;
    }
    if (this.openDropdownId !== null && !target.closest('.action-cell-wrapper')) {
      this.closeActionDropdown();
    }
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(): void {
    if (this.openDropdownId !== null) {
      this.closeActionDropdown();
    }
  }

  onPageChange(page: number): void {
    this.pagination = {
      ...this.pagination,
      currentPage: page
    };
    this.loadUsers();
  }

  onPageSizeChange(pageSize: number): void {
    this.pagination = {
      ...this.pagination,
      pageSize: pageSize,
      currentPage: 1
    };
    this.loadUsers();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const statusColumn = this.columns.find(col => col.key === 'accountStatus');
      if (statusColumn && this.statusCellTemplate) {
        statusColumn.cellTemplate = this.statusCellTemplate;
      }
      const actionColumn = this.columns.find(col => col.key === 'action');
      if (actionColumn && this.actionCellTemplate) {
        actionColumn.cellTemplate = this.actionCellTemplate;
      }
    }, 100);
  }
}
